import React from "react";
import Card from "../../components/page/Card";
import GerenciadorDataTable from "./GerenciadorNFSe/GerenciadorDataTable";
import GerenciadorFiltros from "./GerenciadorNFSe/GerenciadorFiltros";
import {
  hoje,
  primeiroDiaDoMes,
  ultimoDiaDoMes,
} from "../../helpers/mesesHelper";
import NotaFiscalApiClient from "../../api/notaFiscal";
import { AppContext } from "../../contexts/AppContext";
import useApi from "../../hooks/useApi";
import UploadModal from "./GerenciadorNFSe/UploadModal";
import VisualizarNFSeModal from "./GerenciadorNFSe/VisualizarNFSeModal";
import EnviarEmailModal from "./GerenciadorNFSe/EnviarEmailModal";
import ErrorSwal from "../../components/Swal/errorSwal";
import CommonSwal from "../../components/Swal/commonSwal";
import OptionsSwal from "../../components/Swal/optionsSwal";
import InputSwal from "../../components/Swal/inputSwal";
import DownloadMassaModal from "./GerenciadorNFSe/DownloadMassaModal";
import Validation from "../../helpers/Validation";
import { debounce } from "lodash";
import ClienteApiClient from "../../api/cliente";
import VisualizarJsonModal from "./GerenciadorNFSe/VisualizarJsonModal";
import EmailMassaModal from "./GerenciadorNFSe/EmailMassaModal";
import CommonsApiClient from "../../api/commons";

function GerenciadorNFSe() {
  const [filtros, setFiltros] = React.useState({
    status: "TODAS",
    inicio: primeiroDiaDoMes,
    fim: ultimoDiaDoMes,
  });
  const [linhas, setLinhas] = React.useState([]);
  const [uploadModal, setUploadModal] = React.useState(false);
  const [values, setValues] = React.useState({});
  const [visualizarDocumentoModal, setVisualizarDocumentoModal] =
    React.useState(false);
  const [visualizarJsonModal, setVisualizarJsonModal] = React.useState(false);
  const [checkboxesMarcados, setCheckboxesMarcados] = React.useState([]);
  const [enviarEmailModal, setEnviarEmailModal] = React.useState(false);
  const [downloadEmMassaModal, setDownloadEmMassaModal] = React.useState(false);
  const [emailEmMassaModal, setEmailEmMassaModal] = React.useState(false);
  const [clientes, setClientes] = React.useState([]);
  const { session } = React.useContext(AppContext);

  const getNotasFiscaisCb = React.useCallback(
    (inicio = null, fim = null, status = null) => {
      if (!filtros.status && !status) return;
      return NotaFiscalApiClient.getNotasFiscais(
        session?.empresa.replace(/E/gi, ""),
        {
          data_inicial: inicio ? inicio : filtros.inicio,
          data_final: fim ? fim : filtros.fim,
          status: status ? status : filtros.status,
        }
      );
    },
    [filtros.fim, filtros.inicio, filtros.status, session?.empresa]
  );

  const { request: atualizarNotasFiscais, data: notasFiscaisResponse } =
    useApi(getNotasFiscaisCb);
  const postNotasFiscaisApi = useApi(NotaFiscalApiClient.postNotasFiscais);
  const getNotaFiscalApi = useApi(NotaFiscalApiClient.getNotaFiscal);
  const solicitarCancelamentoApi = useApi(
    NotaFiscalApiClient.solicitarCancelamento
  );
  const deleteNotaFiscalApi = useApi(NotaFiscalApiClient.deleteNotaFiscal);
  const enviarEmailsApi = useApi(NotaFiscalApiClient.enviarEmails);
  const downloadEmMassaApi = useApi(NotaFiscalApiClient.downloadEmMassa);
  const cancelarNotaApi = useApi(NotaFiscalApiClient.cancelarNota);
  const emailEmMassaApi = useApi(NotaFiscalApiClient.emailEmMassa);
  const { request: getClientes, data: clientesResponse } = useApi(
    ClienteApiClient.listClientes
  );
  const atualizarNotaApi = useApi(NotaFiscalApiClient.atualizarNotaFiscal);
  const cancelarNotaPrefeituraApi = useApi(
    NotaFiscalApiClient.cancelarNotaPrefeitura
  );
  const sincronizarNotasApi = useApi(NotaFiscalApiClient.sincronizarNotas);
  const documentoRouteApi = useApi(CommonsApiClient.documentoRoute);

  const getNotas = React.useMemo(
    () =>
      debounce(() => {
        atualizarNotasFiscais();
      }, 600),
    [atualizarNotasFiscais]
  );

  //Busca notas fiscais ao renderizar tela
  React.useEffect(() => {
    if (session?.empresa) {
      getNotas();

      return () => {
        getNotas.cancel();
      };
    }
  }, [getNotas, session?.empresa]);

  //Define o retorno da api nos valores das linhas da tabela
  React.useEffect(() => {
    if (notasFiscaisResponse) setLinhas(notasFiscaisResponse.notas);
  }, [notasFiscaisResponse]);

  const togUploadModal = () => {
    setValues({});
    setUploadModal(true);
  };

  //Busca clientes ao renderizar tela
  React.useEffect(() => {
    if (session.empresa) getClientes(session.empresa.replace(/E/gi, ""));
  }, [getClientes, session.empresa]);

  //Define estado clientes ao receber clientes
  React.useEffect(() => {
    if (clientesResponse) setClientes(clientesResponse);
  }, [clientes, clientesResponse]);

  const togVisualizarModal = async (uid) => {
    const response = await getNotaFiscalApi.request(
      session?.empresa.replace(/E/gi, ""),
      uid,
      {
        ext: "pdf",
        type: "visualizar",
      }
    );
    if (!response) return;
    const pdfBlob = new Blob([response], { type: "application/pdf" });
    const documentUrl = URL.createObjectURL(pdfBlob);

    setValues({ documento: documentUrl });
    setVisualizarDocumentoModal(true);
  };

  const togEnviarEmailModal = (uid, eno) => {
    setValues({
      ...values,
      uid: uid,
      eno: eno === "ENO",
      xml: true,
      pdf: true,
    });
    setEnviarEmailModal(true);
  };

  const togDownloadMassaModal = () => {
    setValues({
      download_pdf: true,
      download_xml: true,
      opcao_download: true,
    });
    setDownloadEmMassaModal(true);
  };

  const togEmailMassaModal = () => {
    setValues({
      download_pdf: true,
      download_xml: true,
      opcao_envio: true,
    });
    setEmailEmMassaModal(true);
  };

  const uploadNotaFiscal = async () => {
    const formData = new FormData();
    formData.append("tipo_nota", values.tipo_nota);
    formData.append("numero_nota", values.numero_nota);
    formData.append("valor_nota", values.valor_nota);
    formData.append("data_emissao", values.data_emissao);
    formData.append("tipo_mercado", values.tipo_mercado);
    formData.append("arquivo", values.arquivo);
    formData.append("nome_tomador", values.nome);
    if (Validation.verificarForm(formData))
      return ErrorSwal({
        text: "Favor preencher todos os dados obrigatórios.",
      });
    formData.append("uid_tomador", values?.tomador ?? null);

    const response = await postNotasFiscaisApi.request(
      session?.empresa.replace(/E/gi, ""),
      formData
    );

    if (!response) return;
    CommonSwal({
      icon: "success",
      title: "Nota fiscal salva com sucesso!",
    }).then(async () => {
      setUploadModal(false);
      await atualizarNotasFiscais();
    });
  };

  const downloadNotaFiscal = async (uid, formato, descricao) => {
    const response = await getNotaFiscalApi.request(
      session?.empresa.replace(/E/gi, ""),
      uid,
      {
        ext: formato,
        type: "download",
      }
    );
    if (!response) return;
    const blob = new Blob([response]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${descricao}.${formato}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const deleteNotaFiscal = (uid) => {
    OptionsSwal({
      title: "Tem certeza que deseja excluir este arquivo?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteNotaFiscalApi.request(
          session?.empresa.replace(/E/gi, ""),
          uid
        );
        if (!response) return;
        CommonSwal({
          icon: "success",
          title: "Nota fiscal excluída com sucesso!",
        }).then(async () => {
          await atualizarNotasFiscais();
        });
      }
    });
  };

  const inativarNotaFiscal = async (uid) => {
    const response = await deleteNotaFiscalApi.request(
      session?.empresa.replace(/E/gi, ""),
      uid
    );
    if (!response) return;
    CommonSwal({
      icon: "success",
      title: "Nota fiscal inativada com sucesso!",
      text: "Notas inativas não irão aparecer na listagem",
    }).then(async () => {
      await atualizarNotasFiscais();
    });
  };

  const cancelarNotaFiscal = async (uid) => {
    InputSwal({
      title: "Deseja cancelar a nota fiscal de serviço?",
      icon: "warning",
      input: "text",
      inputLabel: "Justificativa",
      requiredMessage: "Preencher justificativa.",
      required: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (result.value) {
          const formData = new FormData();
          formData.append("justificativa", result.value);
          formData.append("username", session?.who);
          const response = await cancelarNotaApi.request(
            session?.empresa.replace(/E/gi, ""),
            formData,
            uid
          );
          if (!response) return;
          CommonSwal({
            icon: "success",
            title: "Cancelamento solicitado com sucesso.",
          }).then(async () => {
            await atualizarNotasFiscais();
          });
        }
      }
    });
  };

  const solicitarCancelamento = (uid, tipoNota, mesAnterior) => {
    InputSwal({
      title: "Deseja solicitar o cancelamento da nota fiscal de serviço?",
      icon: "warning",
      input: "text",
      inputLabel: "Justificativa",
      requiredMessage: "Preencher justificativa.",
      required: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (result.value) {
          const formData = new FormData();
          formData.append("tipo_nota", tipoNota);
          formData.append("uid_usuario", session?.user_guid);
          formData.append("justificativa", result.value);
          const response = await solicitarCancelamentoApi.request(
            session?.empresa.replace(/E/gi, ""),
            uid,
            formData
          );
          if (!response) return;
          CommonSwal({
            icon: "success",
            title: "Cancelamento da NFS-e solicitado.",
            text: mesAnterior
              ? "O cancelamento de notas fiscais de meses anteriores não é realizado automaticamente. A solicitação foi enviada para que nossa equipe verifique a possibilidade de cancelamento."
              : "A sua solicitação de cancelamento de NFS-e foi realizado. Informaremos quando a NFS-e for cancelada com sucesso.",
          }).then(async () => {
            await atualizarNotasFiscais();
          });
        }
      }
    });
  };

  const downloadMassa = async () => {
    if (values.download_pdf === false && values.download_xml === false)
      return ErrorSwal({
        text: "Selecione pelo menos um tipo de arquivo.",
      });

    const formData = new FormData();

    if (values.opcao_download) {
      if (checkboxesMarcados.length === 0)
        return ErrorSwal({
          text: "Nenhuma nota marcada.",
        });

      formData.append("uids", JSON.stringify(checkboxesMarcados));
    } else {
      const idsParaAppend = linhas.map((linha) => linha.uid);

      if (idsParaAppend.length === 0) {
        return ErrorSwal({
          text: "Não existem notas para realizar download.",
        });
      }

      formData.append("uids", JSON.stringify(idsParaAppend));
    }

    formData.append("pdf", values.download_pdf ? 1 : 0);
    formData.append("xml", values.download_xml ? 1 : 0);

    const response = await downloadEmMassaApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    const blob = new Blob([response], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Notas fiscais - ${hoje
      .toLocaleDateString("pt-BR")
      .replace(/\//g, "-")}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setDownloadEmMassaModal(false);
  };

  const enviarEmails = async () => {
    if (values?.eno && values.xml === false && values.pdf === false)
      return ErrorSwal({
        text: "Não é possível enviar um e-mail sem selecionar pelo menos um tipo de arquivo.",
      });

    if (!values.emails)
      return ErrorSwal({
        text: "Nenhum email informado.",
      });

    var emailsArray = values.emails.replace(/ /g, "");
    emailsArray = emailsArray.split(",");

    const formData = new FormData();
    formData.append("uid", values.uid);
    formData.append("emails", JSON.stringify(emailsArray));
    formData.append("pdf", 1);
    formData.append("xml", 1);

    const emails = values.emails;
    const erros = Validation.validarEmails(emails);
    if (erros)
      return ErrorSwal({
        text: "Por favor, forneça email(s) válido(s).",
      });

    const response = await enviarEmailsApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "E-mail enviado com sucesso!",
      icon: "success",
    }).then(async () => {
      setEnviarEmailModal(false);
      await atualizarNotasFiscais();
    });
  };

  const atualizarNota = async (uid) => {
    await atualizarNotaApi.request(session?.empresa.replace(/E/gi, ""), uid);
    await atualizarNotasFiscais();
    // CommonSwal({
    //   title: "Notas fiscais atualizadas com sucesso!",
    //   icon: "success",
    // });
  };

  const togVisualizarJson = async (json) => {
    setValues({ json });
    setVisualizarJsonModal(true);
  };

  const cancelarPrefeitura = async (uid) => {
    OptionsSwal({
      title: "Deseja cancelar a nota fiscal de serviço?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await cancelarNotaPrefeituraApi.request(
          session?.empresa.replace(/E/gi, ""),
          uid
        );
        if (!response) return;
        CommonSwal({
          icon: "success",
          title: "Nota fiscal cancelada com sucesso.",
        }).then(async () => {
          await atualizarNotasFiscais();
        });
      }
    });
  };

  const emailMassa = async () => {
    if (values.download_pdf === false && values.download_xml === false)
      return ErrorSwal({
        text: "Selecione pelo menos um tipo de arquivo.",
      });

    const formData = new FormData();

    if (values.opcao_envio) {
      if (checkboxesMarcados.length === 0)
        return ErrorSwal({
          text: "Nenhuma nota marcada.",
        });

      formData.append("uids", JSON.stringify(checkboxesMarcados));
    } else {
      const idsParaAppend = linhas.map((linha) => linha.uid);

      if (idsParaAppend.length === 0) {
        return ErrorSwal({
          text: "Não existem notas para envio.",
        });
      }

      formData.append("uids", JSON.stringify(idsParaAppend));
    }

    formData.append("pdf", values.download_pdf ? 1 : 0);
    formData.append("xml", values.download_xml ? 1 : 0);

    const response = await emailEmMassaApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "E-mails enviados com sucesso!",
      icon: "success",
    }).then(async () => {
      setEmailEmMassaModal(false);
      await atualizarNotasFiscais();
    });
  };

  const sincronizarNotas = async () => {
    OptionsSwal({
      title: "Deseja sincronizar as notas fiscais?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const hoje = new Date();
        const mes = String(hoje.getMonth() + 1).padStart(2, "0");
        const ano = hoje.getFullYear();
        const dataInicial = `01/${mes}/${ano}`;
        const dataFinal = `${String(hoje.getDate()).padStart(2, "0")}/${mes}/${ano}`;
        const data = {
          data_inicial: dataInicial,
          data_final: dataFinal
        };
        const response = await sincronizarNotasApi.request(
          session?.empresa.replace(/E/gi, ""),
          data
        );
        if (!response) return;
        CommonSwal({
          icon: "success",
          title: "Notas fiscais sincronizadas com sucesso.",
          text: response?.message?.message || null,
        }).then(async () => {
          await atualizarNotasFiscais();
        });
      }
    });
  };

  const downloadDocumento = async (caminho, ext, descricao) => {
    const response = await documentoRouteApi.request(caminho, {
      ext: ext,
      type: "download",
    });
    if (!response) return;
    const blob = new Blob([response]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${descricao}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Card>
        <GerenciadorFiltros filtrosHook={{ filtros, setFiltros }} />
        <GerenciadorDataTable
          linhas={linhas}
          togUploadModal={togUploadModal}
          togVisualizarModal={togVisualizarModal}
          checkboxesHook={{ checkboxesMarcados, setCheckboxesMarcados }}
          togEnviarEmailModal={togEnviarEmailModal}
          downloadNotaFiscal={downloadNotaFiscal}
          deleteNotaFiscal={deleteNotaFiscal}
          inativarNotaFiscal={inativarNotaFiscal}
          cancelarNotaFiscal={cancelarNotaFiscal}
          solicitarCancelamento={solicitarCancelamento}
          togDownloadMassaModal={togDownloadMassaModal}
          atualizarNota={atualizarNota}
          togVisualizarJson={togVisualizarJson}
          cancelarPrefeitura={cancelarPrefeitura}
          togEmailMassaModal={togEmailMassaModal}
          sincronizarNotas={sincronizarNotas}
          downloadDocumento={downloadDocumento}
        />
      </Card>
      <UploadModal
        modalHook={{ uploadModal, setUploadModal }}
        valuesHook={{ values, setValues }}
        uploadNotaFiscal={uploadNotaFiscal}
        clientes={clientes}
      />
      <VisualizarNFSeModal
        visualizarModalHook={{
          visualizarDocumentoModal,
          setVisualizarDocumentoModal,
        }}
        documento={values.documento}
      />
      <EnviarEmailModal
        modalHook={{ enviarEmailModal, setEnviarEmailModal }}
        valuesHook={{ values, setValues }}
        enviarEmails={enviarEmails}
      />
      <DownloadMassaModal
        DownloadModalHook={{ downloadEmMassaModal, setDownloadEmMassaModal }}
        valuesHook={{ values, setValues }}
        downloadMassa={downloadMassa}
      />
      <VisualizarJsonModal
        visualizarModalHook={{ visualizarJsonModal, setVisualizarJsonModal }}
        json={values?.json}
      />
      <EmailMassaModal
        emailMassaModalHook={{ emailEmMassaModal, setEmailEmMassaModal }}
        valuesHook={{ values, setValues }}
        emailMassa={emailMassa}
      />
    </>
  );
}

export default GerenciadorNFSe;
